import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@core/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from './snackbar.service';

type THubSpotConversationsApi = {
  widget: {
    load: (options?: { widgetOpen?: boolean }) => void;
    open: () => void;
    close: () => void;
    remove: () => void;
    status: () => { loaded: boolean; pending: boolean };
  };
};

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    HubSpotConversations?: THubSpotConversationsApi;
    hsConversationsOnReady?: Array<() => void>;
    hsConversationsSettings?: { loadImmediately?: boolean };
  }
}

const HUBSPOT_SCRIPT_ELEMENT_ID = 'hs-script-loader';
// Safety net: if the script never calls back (blocked by an ad-blocker, offline, no live
// chatflow configured in HubSpot, ...), don't leave the button stuck in its loading state
// forever - tell the user chat isn't available instead.
const READY_TIMEOUT_MS = 10000;

/**
 * Loads the HubSpot chat widget on demand and opens it.
 *
 * The script isn't loaded up front (index.html), and `loadImmediately` is set to `false`,
 * so the default floating HubSpot launcher bubble never appears - the widget only comes into
 * existence the first time a user actually asks for it (e.g. via the bug report menu's "Chat"
 * option), and only opens once it is fully ready.
 */
@Injectable({
  providedIn: 'root',
})
export class HubspotChatService {
  /** True while the widget script is being fetched for the first time. */
  public readonly loading = signal(false);

  private readonly snackbar = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  private scriptRequested = false;
  private readyTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public openChat(): void {
    if (this.loading() || !environment.hubspot?.enabled || !environment.hubspot.portalId) {
      return;
    }

    if (window.HubSpotConversations?.widget) {
      this.showWidget();

      return;
    }

    this.loading.set(true);
    this.queueOpenOnceReady();
    this.loadScript(environment.hubspot.portalId);

    this.readyTimeoutId = setTimeout(() => {
      this.readyTimeoutId = null;
      this.loading.set(false);
      this.notifyUnavailable();
      // eslint-disable-next-line no-console
      console.warn('HubSpot chat did not become ready in time');
    }, READY_TIMEOUT_MS);
  }

  private showWidget(): void {
    const widget = window.HubSpotConversations?.widget;

    if (!widget) {
      return;
    }

    // open() is a no-op if the widget was never loaded onto the page (which is the case here,
    // since loadImmediately is false) - load({ widgetOpen: true }) both loads and opens it.
    if (widget.status().loaded) {
      widget.open();
    } else {
      widget.load({ widgetOpen: true });
    }
  }

  private notifyUnavailable(): void {
    this.snackbar.openWarning(this.translate.instant('shared.bugReport.chatUnavailable'));
  }

  private clearReadyTimeout(): void {
    if (this.readyTimeoutId !== null) {
      clearTimeout(this.readyTimeoutId);
      this.readyTimeoutId = null;
    }
  }

  private queueOpenOnceReady(): void {
    window.hsConversationsOnReady = window.hsConversationsOnReady ?? [];
    window.hsConversationsOnReady.push(() => {
      this.clearReadyTimeout();
      this.loading.set(false);
      this.showWidget();
    });
  }

  private loadScript(portalId: string): void {
    if (this.scriptRequested || document.getElementById(HUBSPOT_SCRIPT_ELEMENT_ID)) {
      this.scriptRequested = true;

      return;
    }

    this.scriptRequested = true;
    // Suppress the default auto-opening launcher bubble; we drive open/close ourselves.
    window.hsConversationsSettings = { loadImmediately: false };

    const script = document.createElement('script');
    script.id = HUBSPOT_SCRIPT_ELEMENT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = `//js-eu1.hs-scripts.com/${portalId}.js`;

    script.onerror = (): void => {
      script.remove();
      // Drop any callback we queued for this failed attempt so it can't fire late against a
      // future successful load, and reset the flags a retry checks so openChat() actually
      // issues a new request instead of silently no-opping.
      window.hsConversationsOnReady = [];
      this.scriptRequested = false;
      this.clearReadyTimeout();
      this.loading.set(false);
      this.notifyUnavailable();
      // eslint-disable-next-line no-console
      console.warn('Failed to load HubSpot chat script');
    };

    document.head.appendChild(script);
  }
}
